/** A GFM delimiter row: `|---|:--:|`, with or without the outer pipes. */
const DELIMITER_ROW = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
/** A pipe is required too, so a bare `---` (a rule, or a heading's
 *  underline) is never taken for one. */
const isDelimiterRow = (line: string): boolean =>
  line.includes('|') && DELIMITER_ROW.test(line);
/** A fence opener or closer: three or more backticks or tildes. */
const FENCE = /^\s*(`{3,}|~{3,})/;

/** A row of cells: GFM allows the outer pipes to be left off. */
const isTableRow = (line: string): boolean => line.includes('|');

/**
 * Put a blank line before any table that directly follows a line of text.
 *
 * Models often write a table straight under a heading line or a list item
 * ("2) Incomplete submissions (4)" then "| Submission | … |"). Markdown reads
 * those rows as more of that paragraph, so the table renders as text full of
 * pipes. A blank line makes it a table. Code blocks are left alone.
 */
export function separateTables(markdown: string): string {
  if (!markdown.includes('|')) return markdown;
  const lines = markdown.split('\n');
  const out: string[] = [];
  // The open fence's marker: only a run of the same character, at least as
  // long, closes it (so a ~~~ line inside a ``` block stays code).
  let fence: string | null = null;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const marker = FENCE.exec(line)?.[1];
    if (marker && fence === null) {
      fence = marker;
    } else if (
      marker &&
      fence !== null &&
      marker[0] === fence[0] &&
      marker.length >= fence.length
    ) {
      fence = null;
    }
    const inFence = fence !== null || marker !== undefined;
    const previous = out[out.length - 1];
    const startsTable =
      !inFence &&
      isTableRow(line) &&
      isDelimiterRow(lines[i + 1] ?? '') &&
      previous !== undefined &&
      previous.trim() !== '' &&
      !isTableRow(previous);
    if (startsTable) out.push('');
    out.push(line);
  }
  return out.join('\n');
}
